import React from 'react'
import {
  Landmark,
  Layers,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import type { OrgType } from '../../types/branding'

interface OrgTypeOption {
  value: OrgType
  label: string
  description: string
  icon: React.ElementType
}

const ORG_TYPE_OPTIONS: OrgTypeOption[] = [
  {
    value: 'qi',
    label: 'Self QI',
    description: 'Acts as own Qualified Intermediary',
    icon: Landmark,
  },
  {
    value: 'platform',
    label: 'Open1031 Managed QI',
    description: 'Open1031 acts as the QI on your behalf',
    icon: Layers,
  },
]

interface OrgTypeSelectorProps {
  value: OrgType | null | undefined
  onChange: (value: OrgType) => void
  disabled?: boolean
}

export const OrgTypeSelector: React.FC<OrgTypeSelectorProps> = ({
  value,
  onChange,
  disabled,
}) => {
  return (
    <div
      role="radiogroup"
      aria-label="Organization type"
      className="grid grid-cols-1 md:grid-cols-2 gap-3"
    >
      {ORG_TYPE_OPTIONS.map((option) => {
        const selected = value === option.value
        const IconComponent = option.icon
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={option.label}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'border-border hover:border-primary/40 hover:bg-muted/40',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            <IconComponent
              className={cn(
                'h-5 w-5 mt-0.5 shrink-0',
                selected ? 'text-primary' : 'text-muted-foreground',
              )}
              aria-hidden="true"
            />
            <div className="min-w-0">
              <p className={cn(
                'text-sm font-semibold leading-tight',
                selected ? 'text-primary' : 'text-foreground',
              )}>
                {option.label}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                {option.description}
              </p>
            </div>
          </button>
        )
      })}
    </div>
  )
}

export { ORG_TYPE_OPTIONS }
export type { OrgTypeOption }
